import { useEffect, useState } from 'react';
import { LogIn, Moon, Sun } from 'lucide-react';
import { HomePage } from '@/pages/HomePage';
import { WorkbenchPage } from '@/pages/WorkbenchPage';
import { HenniPennyLockup } from '@/brand/HenniPennyMark';
import { WRITING_DESK_URL } from '@/brand/brand';
import { isAuthenticated, redirectToLogin, signOut, getUser } from '@/lib/auth';

type ThemePreference = 'light' | 'dark';

// Dark (navy) is the brand default — the signature flagship look. The toggle
// flips to the cream/paper "light" register. There is deliberately no
// system-preference default: the flagship looks like the flagship on first paint.
const THEME_STORAGE_KEY = 'hennipenny-theme-preference';
const DEFAULT_THEME: ThemePreference = 'dark';

function readStoredThemePreference(): ThemePreference {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  const value = window.localStorage.getItem(THEME_STORAGE_KEY);
  return value === 'light' || value === 'dark' ? value : DEFAULT_THEME;
}

function ThemeToggleButton({ theme, onToggle }: { theme: ThemePreference; onToggle: () => void }) {
  const next = theme === 'dark' ? 'light' : 'dark';
  const Icon = theme === 'dark' ? Sun : Moon;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="mono"
      aria-label={`Switch to ${next} mode`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: 'transparent',
        border: '1px solid var(--theme-border-strong)',
        borderRadius: 'var(--theme-radius)',
        color: 'var(--theme-muted)',
        padding: '5px 10px',
        fontSize: 12,
        cursor: 'pointer',
      }}
    >
      <Icon size={13} />
      {next === 'dark' ? 'Dark' : 'Light'}
    </button>
  );
}

function useHashRoute(): string {
  const [hash, setHash] = useState(() => (typeof window !== 'undefined' ? window.location.hash : ''));
  useEffect(() => {
    const onChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return hash;
}

const goWorkbench = () => {
  window.location.hash = '#/workbench';
};
const goHome = () => {
  window.location.hash = '';
};

export default function App() {
  const hash = useHashRoute();
  const onWorkbench = hash.startsWith('#/workbench');
  const authed = isAuthenticated();
  const user = authed ? getUser() : null;
  const [theme, setTheme] = useState<ThemePreference>(() => readStoredThemePreference());
  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [hash]);

  // A signed-in lab member's home IS the instrument (Director ruling
  // 2026-07-23): the pitch page exists for curious strangers, not members —
  // "Home" must never land a member on marketing. Logged-out visitors keep
  // the pitch (it is the try-it front door from the landing site).
  useEffect(() => {
    if (authed && !onWorkbench) {
      window.location.hash = '#/workbench';
    }
  }, [authed, onWorkbench]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    document.body.style.background = 'var(--theme-bg-outer)';
  }, [theme]);

  const themeToggle = <ThemeToggleButton theme={theme} onToggle={toggleTheme} />;

  if (!onWorkbench) {
    return (
      <HomePage
        onOpenWorkbench={goWorkbench}
        onSignIn={redirectToLogin}
        onSignOut={signOut}
        authed={authed}
        userEmail={user?.email}
        themeToggle={themeToggle}
      />
    );
  }

  return (
    <div className="hp-shell">
      <header className="hp-topbar" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0, flexWrap: 'wrap' }}>
          {!authed && (
            /* Members' home IS this instrument (root redirects here), so the
               button only exists for logged-out visitors trying the demo. */
            <button
              type="button"
              onClick={goHome}
              className="mono"
              style={{
                background: 'transparent',
                border: '1px solid var(--sp-border-strong)',
                borderRadius: 'var(--sp-radius)',
                color: 'var(--sp-text-muted)',
                padding: '5px 10px',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              ← Home
            </button>
          )}
          <HenniPennyLockup size={26} wordSize={18} />
          <span className="hp-topbar-tag">NILM workbench</span>
        </div>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <a
            href={WRITING_DESK_URL}
            className="mono"
            style={{
              border: '1px solid var(--sp-border-strong)',
              borderRadius: 'var(--sp-radius)',
              color: 'var(--sp-text-muted)',
              padding: '5px 10px',
              fontSize: 12,
              textDecoration: 'none',
            }}
          >
            Writing desk →
          </a>
          {themeToggle}
          {authed ? (
            <>
              {user?.email && (
                <span
                  className="mono"
                  style={{
                    fontSize: 11,
                    color: 'var(--sp-text-muted)',
                    maxWidth: 180,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.email}
                </span>
              )}
              <button type="button" onClick={signOut} className="sp-btn" style={{ fontSize: 12, padding: '5px 12px' }}>
                Sign out
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={redirectToLogin}
              className="sp-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '5px 12px' }}
            >
              <LogIn size={13} />
              Sign in
            </button>
          )}
        </nav>
      </header>
      <main className="hp-main">
        <WorkbenchPage />
      </main>
    </div>
  );
}
